/**
 * CompatibilityMatrix — 호환성 매트릭스 (queue #637 · docs/biz-specs/common-showcase.md
 * §UAT-2026-07-07-R637 §R637-4)
 *
 * ModuleDetailBody(InfoPanel · 데모 패널 공유 본문)의 version <dl> 직후에 삽입되어
 * Spring Boot / JDK / eraf-bom / eraf-parent / 이 능력 모듈 버전을 한눈에 보여주고
 * /getting-started 로 딥링크한다. 데이터는 빌드타임 생성물 `data/module-versions.json`
 * (apps/web/scripts/extract-module-versions.mjs)만 소비 — BE 호출 없음(#637 범위 0).
 *
 * node.version 은 BE MavenModuleVersionResolver 가 classpath 에서 조회한 실측(런타임
 * 탑재) 버전이고, moduleVersions.modules[node.artifact] 는 BOM 이 집약한 선언 버전이다.
 * 두 값은 정상 동작 시 일치하지만(drift-guard BE 테스트), classpath 미탑재 모듈은
 * node.version 이 null("미탑재")이라 선언 버전만 보조로 병기한다.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ModuleNode, ModuleVersions } from '../types/showcase';
import moduleVersionsJson from '../data/module-versions.json';

const MODULE_VERSIONS = moduleVersionsJson as unknown as ModuleVersions;

const rowGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 'var(--spacing-1) var(--spacing-4)',
  fontSize: 'var(--font-size-sm)',
};

const dtStyle: React.CSSProperties = { color: 'var(--color-text-muted)', fontWeight: 600 };
const ddStyle: React.CSSProperties = { margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-xs)' };
const codeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-brand-primary)',
};

interface Props {
  node: ModuleNode;
}

export function CompatibilityMatrix({ node }: Props) {
  const { t } = useTranslation();

  // §R637-4: SUPERSEDED 노드는 "이 능력 모듈" 행을 생략(SupersedeNotice 가 후계 안내를 우선 담당).
  const isSuperseded = node.supersededBy != null;
  const declaredVersion = MODULE_VERSIONS.modules[node.artifact];
  const runtimeVersion = node.version || null;

  return (
    <section
      aria-label={t('showcase.compat.title')}
      style={{
        marginBottom: 'var(--spacing-4)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        background: 'var(--color-surface-elevated)',
      }}
    >
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-2) 0',
        }}
      >
        {t('showcase.compat.title')}
      </p>

      <dl style={{ margin: 0 }}>
        <div style={rowGridStyle}>
          <dt style={dtStyle}>{t('showcase.compat.springBoot')}</dt>
          <dd style={ddStyle}><code style={codeStyle}>{MODULE_VERSIONS.springBoot}</code></dd>

          <dt style={dtStyle}>{t('showcase.compat.jdk')}</dt>
          <dd style={ddStyle}><code style={codeStyle}>{MODULE_VERSIONS.jdk}</code></dd>

          <dt style={dtStyle}>{t('showcase.compat.bom')}</dt>
          <dd style={ddStyle}><code style={codeStyle}>{MODULE_VERSIONS.bomVersion}</code></dd>

          <dt style={dtStyle}>{t('showcase.compat.parent')}</dt>
          <dd style={ddStyle}><code style={codeStyle}>{MODULE_VERSIONS.parentVersion}</code></dd>

          {!isSuperseded && (
            <>
              <dt style={dtStyle}>{t('showcase.compat.moduleVersion')}</dt>
              <dd style={ddStyle}>
                <code style={codeStyle}>{runtimeVersion ?? t('showcase.compat.runtimeAbsent')}</code>
                {declaredVersion && (!runtimeVersion || runtimeVersion !== declaredVersion) && (
                  <span style={{ marginLeft: 'var(--spacing-2)', color: 'var(--color-text-muted)' }}>
                    {t('showcase.compat.declaredVersionNote', { version: declaredVersion })}
                  </span>
                )}
              </dd>
            </>
          )}

          <dt style={dtStyle}>{t('showcase.compat.gettingStarted')}</dt>
          <dd style={ddStyle}>
            <Link to="/getting-started" style={{ color: 'var(--color-brand-primary)' }}>
              {t('showcase.compat.gettingStarted')} →
            </Link>
          </dd>
        </div>
      </dl>

      {isSuperseded && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-2)', marginBottom: 0 }}>
          {t('showcase.compat.supersededNote')}
        </p>
      )}
    </section>
  );
}
